// Shared event-detail modal (view / edit / delete).
//
// Originally lived inline in `apps/retailer/src/pages/calendar/page.ts`
// (`openDetail` + `renderDetailEditable` + `renderDetailReadOnly`). Hoisted
// here so the dashboard's mini-calendar widget can open the SAME popup —
// retailers click an event in either surface and get an identical UX,
// without the dashboard having to duplicate the form, the form's CSS,
// the keyboard handling, or the API contract.
//
// Persistence is injected — callers pass `onSave` / `onDelete` callbacks
// because:
//   • The calendar page wants saves to flow through its offline-first
//     IndexedDB repo (optimistic local writes, queued POST/PUT/DELETE).
//   • The dashboard widget has no repo handle, so it just hits the
//     /api/portal/calendar endpoints directly via the shared api client.

import { api } from "@tkos/api-client";
import { confirmDialog } from "@tkos/shared-ui";

export type EventRecurrence = "none" | "daily" | "weekly" | "monthly" | "yearly";

export interface DetailEvent {
  /** Master row id. For a virtual recurrence instance, this is the
   *  derived `master::ymd` id — pass `masterId` separately for delete. */
  id: string;
  title: string;
  type: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM (24h) or "" if all-day */
  time: string;
  endTime: string;
  allDay: boolean;
  notes: string;
  customer: string;
  recurrence: EventRecurrence;
  /** True if the row was synthesised from another portal source. */
  derived?: boolean;
  /** True if the row is read-only for any other reason. */
  readOnly?: boolean;
  /** For a virtual recurrence instance, the underlying master row id.
   *  Save / delete targets this id (so a series update propagates). */
  masterId?: string | null;
  /** Set when this event came from a customer-portal appointment request:
   *  the `bookings` row it belongs to, and that booking's status. A
   *  `bookingStatus` of "pending" puts a Confirm button in the footer so the
   *  retailer can accept it without leaving the calendar. */
  bookingId?: string | null;
  bookingStatus?: string | null;
}

export interface EventDetailDeps {
  /** Toast surface. */
  showToast: (msg: string, kind?: "info" | "success" | "error") => void;
  /** Async save. Receives master id + patch fields. Optional — if
   *  omitted the modal PUTs to `/api/portal/calendar/:id` itself. */
  onSave?: (id: string, patch: SavePatch) => Promise<void>;
  /** Async delete. Receives master id. Optional — if omitted the modal
   *  DELETEs `/api/portal/calendar/:id` itself. */
  onDelete?: (id: string) => Promise<void>;
  /** Optional hook fired after a successful save (post `onSave`). */
  onAfterSave?: () => void;
  /** Optional hook fired after a successful delete. */
  onAfterDelete?: () => void;
}

export interface SavePatch {
  title: string;
  type: string;
  date: string;
  time: string;
  endTime: string;
  allDay: boolean;
  customer: string;
  notes: string;
}

interface EventType {
  id: string;
  label: string;
  color: string;
}

// Event types — verbatim from calendar/page.ts so colours and labels
// match. If you add a type there, mirror it here.
const TYPES: ReadonlyArray<EventType> = [
  { id: "enquiry",      label: "New Enquiries",            color: "#e58200" },
  { id: "delivery",     label: "Deliveries",               color: "#3b82f6" },
  { id: "booking",      label: "Bookings / Consultations", color: "#22c55e" },
  { id: "installation", label: "Installations",            color: "#a855f7" },
  { id: "personal",     label: "Personal",                 color: "#8a8f99" },
  { id: "followup",     label: "Follow-ups",               color: "#fbbf24" },
  { id: "survey",       label: "Site Surveys",             color: "#14b8a6" },
  { id: "snag",         label: "Snags",                    color: "#f59e0b" },
  { id: "payment",      label: "Payments",                 color: "#ef4444" },
  { id: "team",         label: "Team",                     color: "#6366f1" },
];

const TYPE_BY_ID: Readonly<Record<string, EventType>> = TYPES.reduce(
  (acc, t) => ({ ...acc, [t.id]: t }),
  {} as Record<string, EventType>,
);

const REC_LABELS: Record<EventRecurrence, string> = {
  none: "Does not repeat",
  daily: "Repeats daily",
  weekly: "Repeats weekly",
  monthly: "Repeats monthly",
  yearly: "Repeats yearly",
};

const DAY_LONG_FMT = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

function pad(n: number): string { return n < 10 ? `0${n}` : String(n); }
function parseYMD(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y || 1970, (m || 1) - 1, d || 1);
}
function fmtTime12(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const period = h >= 12 ? "pm" : "am";
  const h12 = ((h + 11) % 12) + 1;
  return m === 0 ? `${h12}${period}` : `${h12}:${pad(m)}${period}`;
}
function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Singleton modal (mounted lazily) ─────────────────────────────────────────
//
// This used to be a native <dialog> opened with showModal(). That put it in
// the browser's TOP LAYER, which (a) sits above all positioned content
// regardless of z-index and (b) makes everything outside it `inert`. So the
// shared confirmDialog (a z-index overlay <div>) rendered BEHIND this popup AND
// was unclickable. We now use the same plain-overlay pattern as the rest of the
// app (a fixed <div> backdrop + panel) at a z-index BELOW the shared
// `--z-overlay` (1000) token, so confirm/alert/prompt dialogs always stack on
// top and remain clickable.

let dialog: HTMLDivElement | null = null;
let bodyEl: HTMLElement | null = null;
let footEl: HTMLElement | null = null;
let titleEl: HTMLElement | null = null;

function onOverlayKeydown(e: KeyboardEvent): void {
  if (e.key === "Escape") closeDetail();
}

function ensureDialog(): HTMLDivElement {
  if (dialog) return dialog;
  const overlay = document.createElement("div");
  overlay.className = "cal-modal-overlay tkos-event-detail-overlay";
  overlay.innerHTML = `
    <div class="cal-modal cal-detail-modal tkos-event-detail" role="dialog" aria-modal="true" tabindex="-1">
      <div class="cal-modal__form">
        <header class="cal-modal__head">
          <h2 data-role="ed-title">Event</h2>
          <button type="button" class="cal-modal__close" data-role="ed-close" aria-label="Close">&times;</button>
        </header>
        <div class="cal-modal__body" data-role="ed-body"></div>
        <footer class="cal-modal__footer" data-role="ed-foot"></footer>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  dialog = overlay;
  bodyEl = overlay.querySelector<HTMLElement>("[data-role='ed-body']");
  footEl = overlay.querySelector<HTMLElement>("[data-role='ed-foot']");
  titleEl = overlay.querySelector<HTMLElement>("[data-role='ed-title']");
  overlay.querySelector<HTMLButtonElement>("[data-role='ed-close']")
    ?.addEventListener("click", () => closeDetail());
  // Click-outside-to-close — only when the backdrop itself (not the panel) is clicked.
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeDetail();
  });
  return overlay;
}

function closeDetail(): void {
  if (dialog) dialog.classList.remove("cal-modal-overlay--open");
  document.removeEventListener("keydown", onOverlayKeydown);
}

function renderReadOnly(ev: DetailEvent, meta: EventType): void {
  if (!bodyEl || !footEl) return;
  const date = parseYMD(ev.date);
  const timeStr = ev.allDay
    ? "All day"
    : ev.time
      ? fmtTime12(ev.time) + (ev.endTime ? ` – ${fmtTime12(ev.endTime)}` : "")
      : "—";

  const rows: string[] = [
    `<dl class="cal-detail__grid">
      <dt>Type</dt><dd><span class="cal-detail__pill" style="background:${meta.color}">${esc(meta.label)}</span></dd>
      <dt>Date</dt><dd>${esc(DAY_LONG_FMT.format(date))}</dd>
      <dt>Time</dt><dd>${esc(timeStr)}</dd>`,
  ];
  if (ev.customer) rows.push(`<dt>Customer</dt><dd>${esc(ev.customer)}</dd>`);
  if (ev.recurrence !== "none") rows.push(`<dt>Repeat</dt><dd>${esc(REC_LABELS[ev.recurrence])}</dd>`);
  if (ev.notes) rows.push(`<dt>Notes</dt><dd class="cal-detail__notes">${esc(ev.notes).replace(/\n/g, "<br>")}</dd>`);
  rows.push(`<dt>Source</dt><dd>${esc(meta.label)} — edit on its source page</dd>`);
  rows.push("</dl>");
  bodyEl.innerHTML = rows.join("");

  footEl.innerHTML =
    `<span class="cal-detail__readonly">Read-only — open the source page to edit.</span>
     <button type="button" class="cal-btn cal-btn--primary" data-role="ed-cancel">Close</button>`;

  footEl.querySelector<HTMLButtonElement>("[data-role='ed-cancel']")
    ?.addEventListener("click", () => closeDetail());
}

async function defaultSave(id: string, patch: SavePatch): Promise<void> {
  await api<{ ok: boolean }>(`/portal/calendar/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
    headers: { "content-type": "application/json" },
  });
}

/** Pull a human-readable reason out of a failed delete. Both the api-client
 *  `ApiError` and the offline repo throw an Error whose `message` is the raw
 *  response body — usually `{ "ok": false, "error": "…" }`. Parse that out;
 *  fall back to a generic message when there's nothing useful. */
function deleteErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  try {
    const parsed = JSON.parse(raw) as { error?: unknown };
    if (parsed && typeof parsed.error === "string" && parsed.error.trim()) {
      return parsed.error;
    }
  } catch {
    /* message wasn't JSON — fall through */
  }
  return "Could not delete event";
}

async function defaultDelete(id: string): Promise<void> {
  await api<{ ok: boolean }>(`/portal/calendar/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

function renderEditable(ev: DetailEvent, deps: EventDetailDeps): void {
  if (!bodyEl || !footEl) return;
  const isSeries = ev.recurrence !== "none" || !!ev.masterId;
  const masterId = ev.masterId ?? ev.id;

  const recurrenceLine = ev.recurrence !== "none"
    ? `<p class="cal-detail__series-note">${esc(REC_LABELS[ev.recurrence])} — editing changes every occurrence.</p>`
    : "";

  bodyEl.innerHTML = `
    <form class="cal-detail-form" data-role="ed-form" novalidate>
      ${recurrenceLine}
      <div class="cal-field">
        <label>Title</label>
        <input type="text" data-field="title" required />
      </div>
      <div class="cal-field">
        <label>Type</label>
        <select data-field="type">
          ${TYPES.map((t) => `<option value="${esc(t.id)}">${esc(t.label)}</option>`).join("")}
        </select>
      </div>
      <div class="cal-field">
        <label class="cal-checkbox">
          <input type="checkbox" data-field="allDay" />
          All-day event
        </label>
      </div>
      <div class="cal-field-row">
        <div class="cal-field">
          <label>Date</label>
          <input type="date" data-field="date" required />
        </div>
        <div class="cal-field" data-role="ed-time-field">
          <label>Start time</label>
          <input type="time" data-field="time" />
        </div>
        <div class="cal-field" data-role="ed-endtime-field">
          <label>End time <span class="cal-field__hint">(optional)</span></label>
          <input type="time" data-field="endTime" />
        </div>
      </div>
      <div class="cal-field">
        <label>Customer <span class="cal-field__hint">(optional)</span></label>
        <input type="text" data-field="customer" />
      </div>
      <div class="cal-field">
        <label>Notes <span class="cal-field__hint">(optional)</span></label>
        <textarea rows="3" data-field="notes"></textarea>
      </div>
    </form>
  `;

  const form = bodyEl.querySelector<HTMLFormElement>("[data-role='ed-form']")!;
  const $f = <T extends HTMLElement>(name: string): T => form.querySelector<T>(`[data-field='${name}']`)!;

  const titleI = $f<HTMLInputElement>("title");
  const typeI  = $f<HTMLSelectElement>("type");
  const dateI  = $f<HTMLInputElement>("date");
  const timeI  = $f<HTMLInputElement>("time");
  const endI   = $f<HTMLInputElement>("endTime");
  const allDayI= $f<HTMLInputElement>("allDay");
  const custI  = $f<HTMLInputElement>("customer");
  const notesI = $f<HTMLTextAreaElement>("notes");
  const timeField = form.querySelector<HTMLDivElement>("[data-role='ed-time-field']")!;
  const endField  = form.querySelector<HTMLDivElement>("[data-role='ed-endtime-field']")!;

  titleI.value = ev.title;
  typeI.value = ev.type;
  dateI.value = ev.date;
  timeI.value = ev.time || "";
  endI.value = ev.endTime || "";
  allDayI.checked = ev.allDay;
  custI.value = ev.customer || "";
  notesI.value = ev.notes || "";

  const syncAllDay = (): void => {
    const ad = allDayI.checked;
    timeField.classList.toggle("cal-field--hidden", ad);
    endField.classList.toggle("cal-field--hidden", ad);
    if (ad) {
      timeI.value = "";
      endI.value = "";
    }
  };
  syncAllDay();
  allDayI.addEventListener("change", syncAllDay);

  const deleteLabel = isSeries ? "Delete series" : "Delete event";
  // A booking the customer requested and nobody has accepted yet. Confirming
  // is the one thing the retailer always wants to do from here, and until now
  // it meant leaving the calendar for the Bookings / Consultations page.
  const pendingBooking = ev.bookingId && String(ev.bookingStatus || "") === "pending";

  footEl.innerHTML = `
    <button type="button" class="cal-btn cal-btn--danger" data-role="ed-delete">${deleteLabel}</button>
    ${pendingBooking ? `<span class="cal-detail__pending">Pending confirmation</span>` : ""}
    <button type="button" class="cal-btn" data-role="ed-cancel">Cancel</button>
    ${pendingBooking ? `<button type="button" class="cal-btn cal-btn--confirm" data-role="ed-confirm">Confirm booking</button>` : ""}
    <button type="button" class="cal-btn cal-btn--primary" data-role="ed-save">Save changes</button>
  `;

  footEl
    .querySelector<HTMLButtonElement>("[data-role='ed-confirm']")
    ?.addEventListener("click", async () => {
      const btn = footEl?.querySelector<HTMLButtonElement>("[data-role='ed-confirm']");
      if (btn) { btn.disabled = true; btn.textContent = "Confirming…"; }
      try {
        // Same call the Bookings page makes, so both surfaces stay in step.
        await api<{ ok: boolean }>(`/portal/bookings/${encodeURIComponent(String(ev.bookingId))}`, {
          method: "PUT",
          body: JSON.stringify({ status: "confirmed" }),
          headers: { "content-type": "application/json" },
        });
        ev.bookingStatus = "confirmed";
        deps.onAfterSave?.();
        deps.showToast("Booking confirmed", "success");
        closeDetail();
      } catch (err) {
        console.error("[event-detail] confirm booking failed", err);
        deps.showToast("Could not confirm the booking", "error");
        if (btn) { btn.disabled = false; btn.textContent = "Confirm booking"; }
      }
    });

  footEl
    .querySelector<HTMLButtonElement>("[data-role='ed-cancel']")
    ?.addEventListener("click", () => closeDetail());

  footEl
    .querySelector<HTMLButtonElement>("[data-role='ed-delete']")
    ?.addEventListener("click", async () => {
      const confirmMsg = isSeries
        ? `Delete "${ev.title}" and every recurrence? It can be recovered for up to 90 days.`
        : `Delete "${ev.title}"? It can be recovered for up to 90 days.`;
      if (!(await confirmDialog(confirmMsg, {
        title: "Delete event",
        confirmLabel: "Delete",
        destructive: true,
      }))) return;
      const delBtn = footEl?.querySelector<HTMLButtonElement>("[data-role='ed-delete']");
      if (delBtn) {
        delBtn.disabled = true;
        delBtn.textContent = "Deleting…";
      }
      try {
        const doDelete = deps.onDelete ?? defaultDelete;
        await doDelete(masterId);
        deps.onAfterDelete?.();
        deps.showToast("Event deleted", "success");
        closeDetail();
      } catch (err) {
        console.error("[event-detail] delete failed", err);
        // Surface the server's actual reason (e.g. a 409 "this event comes from
        // a booking…") instead of a blanket failure. Both delete paths throw an
        // Error whose message is the response body (api-client ApiError and the
        // offline repo both do this), so parse a `{ error }` out of it.
        deps.showToast(deleteErrorMessage(err), "error");
        if (delBtn) {
          delBtn.disabled = false;
          delBtn.textContent = deleteLabel;
        }
      }
    });

  const saveBtn = footEl.querySelector<HTMLButtonElement>("[data-role='ed-save']")!;
  saveBtn.addEventListener("click", async () => {
    const t = titleI.value.trim();
    if (!t) {
      titleI.focus();
      return;
    }
    const date = dateI.value;
    if (!date) {
      dateI.focus();
      return;
    }
    const allDay = allDayI.checked;
    const patch: SavePatch = {
      title: t,
      type: typeI.value,
      date,
      time: allDay ? "" : timeI.value,
      endTime: allDay ? "" : endI.value,
      allDay,
      customer: custI.value.trim(),
      notes: notesI.value.trim(),
    };

    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      const doSave = deps.onSave ?? defaultSave;
      await doSave(masterId, patch);
      deps.onAfterSave?.();
      deps.showToast("Event updated", "success");
      closeDetail();
    } catch (err) {
      console.error("[event-detail] save failed", err);
      deps.showToast("Could not save changes", "error");
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save changes";
    }
  });
}

export function openEventDetail(ev: DetailEvent, deps: EventDetailDeps): void {
  const d = ensureDialog();
  if (titleEl) titleEl.textContent = ev.title || "Event";

  const meta = TYPE_BY_ID[ev.type] ?? TYPE_BY_ID.booking;
  const editable = !ev.derived && !ev.readOnly;

  if (editable) {
    renderEditable(ev, deps);
  } else {
    renderReadOnly(ev, meta!);
  }

  d.classList.add("cal-modal-overlay--open");
  document.addEventListener("keydown", onOverlayKeydown);
  // Focus the panel so Escape works and focus leaves the trigger.
  d.querySelector<HTMLElement>(".cal-modal")?.focus();
}
